// Client-side image compression: resize + re-encode as JPEG to keep uploads small
// Works entirely in the browser using Canvas, no external dependencies.

const MAX_DIMENSION = 1920; // max width or height in pixels
const QUALITY = 0.85; // JPEG quality (0-1)

export const compressImage = (file, { maxDimension = MAX_DIMENSION, quality = QUALITY } = {}) =>
  new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No file'));
    // Non-raster files (svg, etc.) pass through
    if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
      return resolve(file);
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        const scale = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return reject(new Error('Compression failed'));
          // Only keep the compressed version if it actually saves space
          const finalBlob = blob.size < file.size ? blob : file;
          const finalName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
          const outFile = new File([finalBlob], finalName, { type: 'image/jpeg' });
          resolve(outFile);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // If the browser can't decode it, upload the original
      resolve(file);
    };
    img.src = url;
  });

export const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
